// Constant Contact API Integration
// Documentation: https://developer.constantcontact.com/api_guide/index.html

const CC_API_BASE_URL = 'https://api.cc.email/v3';
const CC_ACCESS_TOKEN = process.env.REACT_APP_CONSTANT_CONTACT_ACCESS_TOKEN;
const CC_API_KEY = process.env.REACT_APP_CONSTANT_CONTACT_API_KEY;

// Default headers for API requests
const getHeaders = () => ({
  'Authorization': `Bearer ${CC_ACCESS_TOKEN}`,
  'Content-Type': 'application/json',
  'Cache-Control': 'no-cache'
});

// Contact Lists (for different member tiers and groups)
export const CONTACT_LISTS = {
  ALL_MEMBERS: 'all_members',
  TIER_1: 'tier_1_members',
  TIER_2: 'tier_2_members', 
  TIER_3: 'tier_3_members',
  TIER_4: 'tier_4_members',
  TIER_V: 'tier_v_members',
  NEWSLETTER: 'newsletter_subscribers',
  EVENT_UPDATES: 'event_updates',
  JOB_ALERTS: 'job_alerts',
  PROJECT_ALERTS: 'project_alerts'
};

// Create or update contact in Constant Contact
export const addOrUpdateContact = async (contactData) => {
  try {
    const contact = {
      email_address: {
        address: contactData.email,
        permission_to_send: 'implicit'
      },
      first_name: contactData.firstName || '',
      last_name: contactData.lastName || '',
      job_title: contactData.jobTitle || '',
      company_name: contactData.company || '',
      phone_numbers: contactData.phone ? [{
        phone_number: contactData.phone,
        kind: 'work'
      }] : [],
      custom_fields: [
        {
          custom_field_id: 'membership_tier',
          value: contactData.membershipTier || ''
        },
        {
          custom_field_id: 'member_since',
          value: contactData.memberSince || ''
        },
        {
          custom_field_id: 'chapter',
          value: contactData.chapter || ''
        }
      ],
      list_memberships: contactData.lists || [CONTACT_LISTS.ALL_MEMBERS]
    };

    const response = await fetch(`${CC_API_BASE_URL}/contacts`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(contact)
    });

    if (!response.ok) {
      throw new Error(`Constant Contact API error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error adding contact to Constant Contact:', error);
    throw error;
  }
};

// Add contact to specific list (e.g., when they join a group)
export const addContactToList = async (email, listId) => {
  try {
    // First get the contact
    const contact = await getContactByEmail(email);
    if (!contact) {
      throw new Error('Contact not found');
    }

    // Add to list
    const response = await fetch(`${CC_API_BASE_URL}/contacts/${contact.contact_id}/list_memberships`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        list_id: listId
      })
    });

    if (!response.ok) {
      throw new Error(`Error adding contact to list: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error adding contact to list:', error);
    throw error;
  }
};

// Get contact by email
export const getContactByEmail = async (email) => {
  try {
    const response = await fetch(`${CC_API_BASE_URL}/contacts?email=${encodeURIComponent(email)}`, {
      method: 'GET',
      headers: getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Error fetching contact: ${response.status}`);
    }

    const data = await response.json();
    return data.contacts && data.contacts.length > 0 ? data.contacts[0] : null;
  } catch (error) {
    console.error('Error getting contact:', error);
    throw error;
  }
};

// Create email campaign
export const createEmailCampaign = async (campaignData) => {
  try {
    const campaign = {
      name: campaignData.name,
      email_campaign_activities: [{
        format_type: 5, // HTML format
        from_name: 'Accelerated Professionals Network',
        from_email: 'noreply@the-apn.com',
        reply_to_email: 'info@the-apn.com',
        subject: campaignData.subject,
        html_content: campaignData.htmlContent,
        contact_list_ids: campaignData.listIds || [CONTACT_LISTS.ALL_MEMBERS]
      }]
    };

    const response = await fetch(`${CC_API_BASE_URL}/emails`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(campaign)
    });

    if (!response.ok) {
      throw new Error(`Error creating campaign: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating email campaign:', error);
    throw error;
  }
};

// Send email campaign
export const sendEmailCampaign = async (campaignId) => {
  try {
    const response = await fetch(`${CC_API_BASE_URL}/emails/activities/${campaignId}/schedules`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        scheduled_date: new Date().toISOString()
      })
    });

    if (!response.ok) {
      throw new Error(`Error sending campaign: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error sending email campaign:', error);
    throw error;
  }
};

// Get all contact lists
export const getContactLists = async () => {
  try {
    const response = await fetch(`${CC_API_BASE_URL}/contact_lists`, {
      method: 'GET',
      headers: getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Error fetching lists: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting contact lists:', error);
    throw error;
  }
};

// Create contact list
export const createContactList = async (listName, description = '') => {
  try {
    const response = await fetch(`${CC_API_BASE_URL}/contact_lists`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        name: listName,
        description: description,
        favorite: false
      })
    });

    if (!response.ok) {
      throw new Error(`Error creating list: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating contact list:', error);
    throw error;
  }
};

// Sync APN member to Constant Contact
export const syncMemberToConstantContact = async (userProfile) => {
  try {
    const contactData = {
      email: userProfile.email,
      firstName: userProfile.firstName,
      lastName: userProfile.lastName,
      jobTitle: userProfile.jobTitle,
      company: userProfile.company,
      phone: userProfile.phone,
      membershipTier: userProfile.membershipTier,
      memberSince: userProfile.memberSince,
      chapter: userProfile.chapter,
      lists: [
        CONTACT_LISTS.ALL_MEMBERS,
        // Add to tier-specific list
        userProfile.membershipTier === 'tier1' ? CONTACT_LISTS.TIER_1 :
        userProfile.membershipTier === 'tier2' ? CONTACT_LISTS.TIER_2 :
        userProfile.membershipTier === 'tier3' ? CONTACT_LISTS.TIER_3 :
        userProfile.membershipTier === 'tier4' ? CONTACT_LISTS.TIER_4 :
        userProfile.membershipTier === 'tierUSV' ? CONTACT_LISTS.TIER_V :
        CONTACT_LISTS.ALL_MEMBERS
      ].filter(Boolean)
    };

    return await addOrUpdateContact(contactData);
  } catch (error) {
    console.error('Error syncing member to Constant Contact:', error);
    throw error;
  }
};

// Send announcement via Constant Contact
export const sendAnnouncementViaConstantContact = async (title, message, actionUrl = null) => {
  try {
    // Create HTML content
    const htmlContent = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <div style="background-color: #3d67ad; color: white; padding: 20px; text-align: center;">
          <h1>Accelerated Professionals Network</h1>
        </div>
        <div style="padding: 20px;">
          <h2>${title}</h2>
          <div style="line-height: 1.6;">${message.replace(/\n/g, '<br>')}</div>
          ${actionUrl ? `
            <div style="text-align: center; margin: 30px 0;">
              <a href="${actionUrl}" style="background-color: #3d67ad; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; display: inline-block;">
                Learn More
              </a>
            </div>
          ` : ''}
        </div>
        <div style="background-color: #f5f5f5; padding: 15px; text-align: center; font-size: 12px; color: #666;">
          <p>You received this email because you are a member of APN.</p>
          <p>© Accelerated Professionals Network | All rights reserved.</p>
        </div>
      </div>
    `;

    // Create campaign
    const campaign = await createEmailCampaign({
      name: `APN Announcement: ${title}`,
      subject: `APN Announcement: ${title}`,
      htmlContent: htmlContent,
      listIds: [CONTACT_LISTS.ALL_MEMBERS]
    });

    // Send campaign
    const result = await sendEmailCampaign(campaign.campaign_id);
    
    return {
      success: true,
      campaignId: campaign.campaign_id,
      result: result
    };
  } catch (error) {
    console.error('Error sending announcement via Constant Contact:', error);
    throw error;
  }
};

// Newsletter signup
export const subscribeToNewsletter = async (email, firstName = '', lastName = '') => {
  try {
    return await addOrUpdateContact({
      email: email,
      firstName: firstName,
      lastName: lastName,
      lists: [CONTACT_LISTS.NEWSLETTER]
    });
  } catch (error) {
    console.error('Error subscribing to newsletter:', error);
    throw error;
  }
};

// Event marketing - add contacts to event updates list
export const subscribeToEventUpdates = async (email) => {
  try {
    return await addContactToList(email, CONTACT_LISTS.EVENT_UPDATES);
  } catch (error) {
    console.error('Error subscribing to event updates:', error);
    throw error;
  }
};

// Job alerts subscription
export const subscribeToJobAlerts = async (email) => {
  try {
    return await addContactToList(email, CONTACT_LISTS.JOB_ALERTS);
  } catch (error) {
    console.error('Error subscribing to job alerts:', error);
    throw error;
  }
};

export default {
  addOrUpdateContact,
  addContactToList,
  getContactByEmail,
  createEmailCampaign,
  sendEmailCampaign,
  getContactLists,
  createContactList,
  syncMemberToConstantContact,
  sendAnnouncementViaConstantContact,
  subscribeToNewsletter,
  subscribeToEventUpdates,
  subscribeToJobAlerts
};
